import { Radio } from "antd"
import React, { useId } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import type { Category } from "../types/automation.types"

const CATEGORIES: { key: Category; help: string }[] = [
  { key: "LEAD", help: "categoryLeadHelp" },
  { key: "NURTURING", help: "categoryNurturingHelp" }
]

interface CategoryChoiceProps {
  id?: string
  value?: Category
  onChange?: (category: Category) => void
}

// Lead or Nurturing, as two cards in the channel choice's style, each with its
// one-line explanation. One radiogroup element, as in ChannelChoice; the
// radios share a name, so arrow keys move the choice natively.
function CategoryChoice({ id, value, onChange }: CategoryChoiceProps) {
  const { t } = useTranslation()
  const idPrefix = useId()
  return (
    <div
      id={id}
      role={"radiogroup"}
      aria-label={t("settingsAutomation.builder.basics.categoryLabel")}
      className={"grid w-full grid-cols-1 gap-3 sm:grid-cols-2"}
    >
      {CATEGORIES.map(({ key, help }) => (
        <Radio
          key={key}
          name={`${idPrefix}-category`}
          value={key}
          checked={key === value}
          onChange={() => onChange?.(key)}
          // Named by its title alone; the help is its description.
          aria-labelledby={`${idPrefix}-${key}-label`}
          aria-describedby={`${idPrefix}-${key}-help`}
          className={cn(
            "m-0 flex min-h-12 items-start rounded-lg border border-gray-300 bg-white px-4 py-3",
            "transition-colors hover:border-primary motion-reduce:transition-none",
            key === value && "border-primary ring-1 ring-primary"
          )}
        >
          <span className={"flex flex-col"}>
            <span id={`${idPrefix}-${key}-label`} className={"font-medium"}>
              {t(`settingsAutomation.category.${key}`)}
            </span>
            <span id={`${idPrefix}-${key}-help`} className={"text-xs text-gray-600"}>
              {t(`settingsAutomation.builder.basics.${help}`)}
            </span>
          </span>
        </Radio>
      ))}
    </div>
  )
}

export { type CategoryChoiceProps, CategoryChoice }
